/**
 * @file footer-dnd.ts
 * @description Перетаскивание в «Меню → Подвал» колонками (phase19.16,
 * приёмка Р2а; SB113; uiux-footer-columns §4.6): колонки — за ⋮⋮ в шапке
 * карточки, пункты — внутри колонки и между колонками (несколько
 * контейнеров @dnd-kit), но не в автосписок категорий.
 *
 * Чистые функции «что перетащили → куда отпустили → новое дерево» и «какая
 * цель под указателем» — их проверяют тесты, а компонент только передаёт
 * id. Префиксы id: `col:` — карточка колонки (её перетаскивают), `drop:` —
 * область «бросьте сюда» у пустой колонки (отдельный `useDroppable`: у
 * пустой колонки нет пунктов, на которые можно попасть).
 */

import { closestCenter, pointerWithin, type CollisionDetection, type UniqueIdentifier } from "@dnd-kit/core";

import { locate, moveSibling, moveToColumn, type MenuTreeNode } from "./menu-tree";

/** Префикс id карточки колонки в перетаскивании. */
export const FOOTER_COLUMN_DND_PREFIX = "col:";
/** Префикс id области «бросьте сюда» пустой колонки. */
export const FOOTER_DROP_DND_PREFIX = "drop:";

export const columnDndId = (id: string): string => `${FOOTER_COLUMN_DND_PREFIX}${id}`;
export const columnDropId = (id: string): string => `${FOOTER_DROP_DND_PREFIX}${id}`;

const stripped = (id: string, prefix: string): string | null => (id.startsWith(prefix) ? id.slice(prefix.length) : null);
/** Колонка, если цель — карточка колонки или область пустой колонки. */
const columnOf = (id: string): string | null => stripped(id, FOOTER_COLUMN_DND_PREFIX) ?? stripped(id, FOOTER_DROP_DND_PREFIX);

/** Колонка (индекс верхнего уровня), к которой относится цель: сама карточка, её область или пункт в ней. */
function targetColumn<T extends MenuTreeNode>(tree: readonly T[], overId: string): number {
  const col = columnOf(overId);
  if (col !== null) return tree.findIndex((n) => n.id === col);
  const at = locate(tree, overId);
  return at ? (at.parent < 0 ? at.index : at.parent) : -1;
}

/** Новое дерево после перетаскивания или `null` (ничего не меняется или так нельзя). */
export function footerDrop<T extends MenuTreeNode>(tree: readonly T[], activeId: string, overId: string): T[] | null {
  if (activeId === overId) return null;
  const draggedColumn = stripped(activeId, FOOTER_COLUMN_DND_PREFIX);
  if (draggedColumn !== null) {
    const to = targetColumn(tree, overId);
    return to < 0 ? null : moveSibling(tree, draggedColumn, to);
  }
  const from = locate(tree, activeId);
  if (!from || from.parent < 0) return null;
  const overColumn = columnOf(overId);
  if (overColumn !== null) {
    // Отпустили на карточку или в область пустой колонки — в конец этой колонки.
    return tree[from.parent]!.id === overColumn ? null : moveToColumn(tree, activeId, overColumn);
  }
  const to = locate(tree, overId);
  if (!to || to.parent < 0) return null;
  if (to.parent === from.parent) return moveSibling(tree, activeId, to.index);
  return moveToColumn(tree, activeId, tree[to.parent]!.id, to.index);
}

const has = (prefix: string) => (id: UniqueIdentifier) => String(id).startsWith(prefix);
const isColumn = has(FOOTER_COLUMN_DND_PREFIX);
const isDrop = has(FOOTER_DROP_DND_PREFIX);

/**
 * Куда «попадает» перетаскиваемое (`collisionDetection` для `DndContext`).
 * `closestCenter` по всем целям при переносе пункта внутри колонки часто
 * выбирал саму карточку колонки (она большая, её центр рядом) — и перенос
 * не происходил. Поэтому цели разделены:
 *   - тащат колонку — только карточки колонок;
 *   - тащат пункт — область пустой колонки, если указатель над ней, иначе
 *     ближайший пункт (карточки колонок не участвуют).
 */
export const footerCollision: CollisionDetection = (args) => {
  if (isColumn(args.active.id)) {
    return closestCenter({ ...args, droppableContainers: args.droppableContainers.filter((c) => isColumn(c.id)) });
  }
  const drops = args.droppableContainers.filter((c) => isDrop(c.id));
  const inDrop = drops.length > 0 ? pointerWithin({ ...args, droppableContainers: drops }) : [];
  if (inDrop.length > 0) return inDrop;
  return closestCenter({ ...args, droppableContainers: args.droppableContainers.filter((c) => !isColumn(c.id) && !isDrop(c.id)) });
};
